// The Classic layout's sidebar (#141), as it was before the chat list: the server banner that scrolls away under the
// pinned server-name head, Apps, Collections (collapsible, collectionsSection.ts), Direct messages and the channels by category, with the unread pills.
// Chosen in web/src/sidebars.ts. Deleting the layout: delete components/classic/ and that file's Classic entry.
import { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { flameChannels, newSinceRead, type Channel } from "@bloom/shared";
import { useChannelActivity } from "../../channelActivity.ts";
import { isUnread } from "../../readState.ts";
import { rowMenuHandlers } from "../ChannelRowMenu.tsx";
import { Flame } from "../Flame.tsx";
import { CloseButton, MutedIcon } from "../icons.tsx";
import { ListGlyph, LockIcon } from "../Lists.tsx";
import { ServerMenu } from "../ServerMenu.tsx";
import { classicSections, flatChannels, setIdleCollapsed, useByActivity, useDenseList, useFlatList, useGroupIdle, useIdleCollapsed, useShowApps, useShowCollections } from "./listOptions.ts";
import { splitIdle } from "./idleChannels.ts";
import { useNow } from "../../timestampFormat.ts";
import { useServerBanner } from "./bannerPref.ts";
import type { SidebarProps } from "../SidebarProps.ts";
import { ClassicDmList } from "./ClassicDmList.tsx";
import { collectionsLabel, COLLECTIONS_KEY, shownCollections } from "./collectionsSection.ts";
import { UnreadPills } from "./UnreadPills.tsx";

type Adding = { kind: "channel"; categoryId: string | null } | { kind: "category" };

const collapsedKey = (serverId: string) => `bloom:collapsed:${serverId}`;
function loadCollapsed(serverId: string): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(collapsedKey(serverId)) ?? "{}");
  } catch {
    return {};
  }
}

/**
 * `lastTimes`: each channel's newest message time (ms), for Sort by activity (#297) and Group idle channels (#601).
 * `knownChannels`: the channels that have a Chats row, so the time is known (a channel without one is never idle).
 */
export function ClassicSidebar(p: SidebarProps & { lastTimes: Map<string, number>; knownChannels: Set<string> }) {
  const { serverId, server, channels, categories, read, mentionCounts, muted, activeId, inApps, appSlug, collections, isAdmin } = p;
  const [adding, setAdding] = useState<Adding | null>(null);
  const [draft, setDraft] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => loadCollapsed(serverId));
  const sidebarRef = useRef<HTMLElement>(null);
  const headRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // The server's banner, unless this device turned it off (Settings → Appearance → Server banner).
  const banner = useServerBanner() ? server.banner : null;
  const bannerRef = useRef<HTMLDivElement>(null);
  const showActivity = useChannelActivity();
  const unreadState = { read, me: p.me };
  const textChannels = channels.filter((c) => c.kind !== "dm");

  // The banner scrolls away with the channel list (like Discord's): the server-name head stays pinned over its top,
  // see-through on the picture at first, and turns solid as the banner goes (--banner-p: 0 at the top, 1 once the
  // banner has gone under the head). Written straight to the element on scroll, so scrolling never re-renders.
  const bannerScrolled = () => {
    const side = sidebarRef.current, scroller = scrollRef.current, banner = bannerRef.current, head = headRef.current;
    if (!side || !scroller || !head) return;
    if (!banner) {
      side.style.removeProperty("--banner-p");
      head.classList.remove("solid");
      return;
    }
    const travel = Math.max(1, banner.offsetHeight - head.offsetHeight);
    const pr = Math.min(1, Math.max(0, scroller.scrollTop / travel));
    side.style.setProperty("--banner-p", pr.toFixed(3));
    head.classList.toggle("solid", pr >= 1);
  };
  useLayoutEffect(bannerScrolled, [banner, serverId, p.inSettings]);
  // While the channel list scrolls (phones), rows show no pressed state: the row under the finger when a scroll
  // starts would otherwise light up and stay lit (FR: channels stay highlighted, sometimes the wrong ones).
  const scrollingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const markScrolling = () => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    scroller.classList.add("scrolling");
    if (scrollingTimer.current) clearTimeout(scrollingTimer.current);
    scrollingTimer.current = setTimeout(() => scroller.classList.remove("scrolling"), 180);
  };

  const submitAdding = async () => {
    if (!adding || !draft.trim()) return;
    const err = adding.kind === "category" ? await p.createCategory(draft) : await p.createChannel(draft, adding.categoryId);
    if (err) return setAddError(err);
    setAdding(null);
    setDraft("");
    setAddError(null);
  };
  const startAdding = (a: Adding) => {
    setAdding(a);
    setDraft("");
    setAddError(null);
  };
  const collectionsCollapsed = !!collapsed[COLLECTIONS_KEY];
  // #557: Show Apps / Show Collections (Settings → Appearance). Off, the section leaves this list and the server menu carries it.
  const sections = classicSections(useShowApps(), useShowCollections());
  // #297: Flat channel list, Sort by activity (only while flat) and Dense channel list (Settings → Appearance).
  const flat = useFlatList();
  const byActivity = useByActivity();
  const dense = useDenseList();
  // #601: Group idle channels. One shared minute clock for the whole list (never a timer per row), only while the setting is on:
  // a channel that crosses 24 hours moves on the next minute, and a new message moves it back at once (its row changes).
  const groupIdle = useGroupIdle();
  const idleCollapsed = useIdleCollapsed();
  const now = useNow(groupIdle ? 60_000 : null);
  const toggleCategory = (id: string) => {
    const next = { ...collapsed, [id]: !collapsed[id] };
    setCollapsed(next);
    try {
      localStorage.setItem(collapsedKey(serverId), JSON.stringify(next));
    } catch {
      // private mode: collapse state just won't persist
    }
  };

  const addForm = (a: Adding) =>
    adding && adding.kind === a.kind && (a.kind === "category" || (adding.kind === "channel" && adding.categoryId === (a as { categoryId: string | null }).categoryId)) ? (
      <form onSubmit={(e) => { e.preventDefault(); void submitAdding(); }} style={{ padding: "4px 0" }}>
        <label htmlFor="new-item" className="sr-only">{a.kind === "category" ? "New category name" : "New channel name"}</label>
        <input id="new-item" className="input" style={{ height: 36 }} autoFocus placeholder={a.kind === "category" ? "Category name" : "new-channel"} value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={() => !draft && setAdding(null)} onKeyDown={(e) => e.key === "Escape" && setAdding(null)} />
        {addError && <div className="error" style={{ fontSize: 12 }}>{addError}</div>}
      </form>
    ) : null;

  /** What the sidebar's unread pills (UnreadPills) count a row as: mentions always, plain unreads unless muted. */
  const unreadMark = (id: string, unread: boolean, mentions: number) => (mentions > 0 ? "mention" : unread && !muted.has(id) ? "unread" : undefined);
  // The busiest channels since my last read get a flame instead of the dot (#58): not the open one, not muted ones.
  const newCounts = textChannels.map((c) => ({
    id: c.id,
    count: c.id === activeId || muted.has(c.id) ? 0 : newSinceRead(c, read[c.id] ?? 0),
  }));
  const flames = showActivity ? flameChannels(newCounts, textChannels.length) : new Set<string>();
  const channelLink = (c: Channel) => {
    // Muted (Discord): no unread dot or flame; mention badges stay.
    const unread = isUnread(c, unreadState) && c.id !== activeId && !muted.has(c.id);
    const mentions = c.id !== activeId ? (mentionCounts[c.id] ?? 0) : 0;
    return (
      <Link
        key={c.id}
        to={`/s/${serverId}/${c.id}`}
        onClick={p.onNavigate}
        className={`channel${c.id === activeId && !inApps ? " active" : ""}${unread ? " unread" : ""}${muted.has(c.id) ? " notify-muted" : ""}`}
        aria-current={c.id === activeId ? "page" : undefined}
        data-unread={unreadMark(c.id, unread, mentions)}
        {...rowMenuHandlers(p.onConversationMenu, c.id)}
      >
        <span className="hash">#</span>
        <span className="channel-name">{c.name}</span>
        {muted.has(c.id) && <MutedIcon className="row-muted" />}
        {mentions > 0 ? (
          <span className="mention-pill" aria-label={`${mentions} unread mention${mentions === 1 ? "" : "s"}`}>{mentions > 99 ? "99+" : mentions}</span>
        ) : unread && flames.has(c.id) ? (
          <Flame count={newCounts.find((n) => n.id === c.id)?.count} />
        ) : (
          unread && <span className="unread-dot" aria-label="unread" />
        )}
      </Link>
    );
  };
  // Idle (#601): the channels the rule takes out of their category (or the flat list), in their own group at the bottom.
  const split = groupIdle
    ? splitIdle({
        channels: textChannels,
        now,
        activeId,
        mentions: (id) => mentionCounts[id] ?? 0,
        byActivity: flat && byActivity,
        newest: (id) => p.lastTimes.get(id),
        known: (id) => p.knownChannels.has(id),
        keepsHeader: isAdmin,
      })
    : null;
  const listed = split ? split.rest : textChannels;
  const inCategory = (id: string | null) => listed.filter((c) => c.categoryId === id).sort((a, b) => a.position - b.position);
  const loose = inCategory(null);
  // Flat: every channel in one list under no header, A to Z or by newest message. Nothing collapses, so nothing is hidden.
  const flatList = flat ? flatChannels(listed, byActivity, (id) => p.lastTimes.get(id)) : [];
  // A category the rule emptied leaves the list, unless the person sees its + (isAdmin); so does the "Channels" header of the loose ones.
  const emptied = (id: string | null) => !!split?.emptied.has(id);
  const idleShown = split ? (idleCollapsed ? split.idle.filter((c) => isUnread(c, unreadState) && c.id !== activeId && !muted.has(c.id)) : split.idle) : [];

  // When the sidebar's unread pills need measuring again: which rows are unread or mentioned, what's collapsed, how many
  // rows there are, and whether the drawer is open.
  const unreadSignature = [
    channels.map((c) => (c.id === activeId ? "" : `${(mentionCounts[c.id] ?? 0) > 0 ? "m" : isUnread(c, unreadState) ? "u" : ""}${muted.has(c.id) ? "x" : ""}`)).join(","),
    channels.length,
    categories.length,
    p.listCount,
    flat ? `flat${byActivity ? "-activity" : ""}` : "",
    dense ? "dense" : "",
    flatList.map((c) => c.id).join(","),
    split ? `idle${idleCollapsed ? "-collapsed" : ""}:${split.idle.map((c) => c.id).join(",")}` : "",
    JSON.stringify(collapsed),
    p.navOpen,
    banner ?? "",
  ].join("|");

  return (
    <aside className={`sidebar${banner ? " has-banner" : ""}`} aria-label="Channels" ref={sidebarRef}>
      <div className={`sidebar-head${banner ? " on-banner" : ""}`} ref={headRef}>
        <ServerMenu
          {...p.menu}
          appsOpen={p.inApps}
          onApps={sections.menuApps ? p.onApps : undefined}
          collections={sections.menuCollections ? collections : undefined}
          openCollection={p.openCollection}
          onCollection={p.onCollection}
          onNewCollection={p.onNewCollection}
        />
        {p.canInvite && <button className="btn btn-small" onClick={p.onInvite}>Invite</button>}
        <CloseButton className="mobile-only" onClick={p.onCloseDrawer} aria-label="Close menu" />
      </div>
      <div className="sidebar-scroll" ref={scrollRef} onScroll={() => { bannerScrolled(); markScrolling(); }}>
        {banner && (
          <div className="sidebar-banner-wrap" ref={bannerRef}>
            <img className="sidebar-banner" src={banner} alt="" width={960} height={540} draggable={false} />
          </div>
        )}
        <div className={`channels${dense ? " dense" : ""}`}>
          {sections.listApps && <Link
            to={`/s/${serverId}/apps`}
            className={`channel apps-link${inApps ? " active" : ""}`}
            aria-current={inApps && !appSlug ? "page" : undefined}
            onClick={p.onCloseDrawer}
          >
            <svg className="apps-link-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3.5" y="3.5" width="7" height="7" rx="2" /><rect x="13.5" y="3.5" width="7" height="7" rx="2" /><rect x="3.5" y="13.5" width="7" height="7" rx="2" /><rect x="13.5" y="13.5" width="7" height="7" rx="2" /></svg>
            Apps
          </Link>}
          {sections.listCollections && <section className="sidebar-lists" aria-label="Collections">
            <div className="category-head">
              <button className="category-toggle" onClick={() => toggleCategory(COLLECTIONS_KEY)} aria-expanded={!collectionsCollapsed}>
                <Chevron collapsed={collectionsCollapsed} />
                <span className="category-name">{collectionsLabel(collections.length, collectionsCollapsed)}</span>
              </button>
              <button className="icon-btn" onClick={p.onNewCollection} aria-label="New collection" title="New collection">+</button>
            </div>
            {shownCollections(collections, collectionsCollapsed, p.openCollection).map((l) => (
              <button
                key={l.id}
                type="button"
                className={`channel${p.openCollection === l.id ? " active" : ""}`}
                onClick={() => p.onCollection(l.id)}
                aria-current={p.openCollection === l.id ? "true" : undefined}
              >
                <ListGlyph list={l} size={14} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.name}</span>
                {l.visibility === "private" && <span className="list-lock" title="Only you"><LockIcon size={11} /></span>}
                <span className="list-count">{l.count || ""}</span>
              </button>
            ))}
            {!collections.length && !collectionsCollapsed && (
              <button type="button" className="channel muted" onClick={p.onNewCollection}>
                <ListGlyph list={{ kind: "custom", emoji: "" }} size={14} />
                Make a collection
              </button>
            )}
          </section>}
          <ClassicDmList
            serverId={serverId}
            dms={channels.filter((c) => c.kind === "dm")}
            me={p.me}
            names={p.names}
            online={p.online}
            activeId={activeId}
            read={read}
            mentionCounts={mentionCounts}
            byChannel={p.byChannel}
            closed={p.dmClosed}
            onNavigate={p.onNavigate}
            onNew={p.onNewDm}
            onClose={(c) => p.onCloseDm(c.id, c.lastSeq)}
            onMenu={p.onConversationMenu}
            muted={muted}
            unreadMark={unreadMark}
          />
          {flat ? (
            <section className="flat-list" aria-label="All channels">
              {flatList.map(channelLink)}
              {addForm({ kind: "channel", categoryId: null })}
            </section>
          ) : (loose.length > 0 || (categories.length === 0 && !emptied(null))) && (
            <>
              {categories.length === 0 && (
                <div className="category-head">
                  <span className="category-name">Channels</span>
                  {isAdmin && <button className="icon-btn" onClick={() => startAdding({ kind: "channel", categoryId: null })} aria-label="Create a channel" title="Create a channel">+</button>}
                </div>
              )}
              {loose.map(channelLink)}
              {addForm({ kind: "channel", categoryId: null })}
            </>
          )}
          {!flat && categories.filter((cat) => !emptied(cat.id)).map((cat) => {
            const isCollapsed = !!collapsed[cat.id];
            const list = inCategory(cat.id);
            // Collapsed, like Discord: still show the open channel and channels with unread messages.
            const shown = isCollapsed ? list.filter((c) => c.id === activeId || isUnread(c, unreadState)) : list;
            return (
              <section key={cat.id} aria-label={cat.name}>
                <div className="category-head">
                  <button className="category-toggle" onClick={() => toggleCategory(cat.id)} aria-expanded={!isCollapsed}>
                    <Chevron collapsed={isCollapsed} />
                    <span className="category-name">{cat.name}</span>
                  </button>
                  {isAdmin && <button className="icon-btn" onClick={() => startAdding({ kind: "channel", categoryId: cat.id })} aria-label={`Create a channel in ${cat.name}`} title="Create a channel">+</button>}
                </div>
                {shown.map(channelLink)}
                {addForm({ kind: "channel", categoryId: cat.id })}
              </section>
            );
          })}
          {split && split.idle.length > 0 && (
            <section className="idle-group" aria-label="Idle">
              <div className="category-head">
                <button className="category-toggle" onClick={() => setIdleCollapsed(!idleCollapsed)} aria-expanded={!idleCollapsed} aria-label={`Idle, ${split.idle.length} channels`}>
                  <Chevron collapsed={idleCollapsed} />
                  <span className="category-name">Idle</span>
                  <span className="idle-count" aria-hidden="true">{split.idle.length}</span>
                </button>
              </div>
              {idleShown.map(channelLink)}
            </section>
          )}
          {isAdmin && (
            <div style={{ padding: "12px 8px 4px" }}>
              {flat
                ? <button className="link-btn" style={{ fontSize: 13 }} onClick={() => startAdding({ kind: "channel", categoryId: null })}>+ New channel</button>
                : adding?.kind === "category" ? addForm({ kind: "category" }) : <button className="link-btn" style={{ fontSize: 13 }} onClick={() => startAdding({ kind: "category" })}>+ New category</button>}
            </div>
          )}
        </div>
      </div>
      <UnreadPills scrollerRef={scrollRef} headRef={headRef} signature={unreadSignature} />
      {p.footer}
    </aside>
  );
}

/** A section's open or collapsed arrow. */
function Chevron({ collapsed }: { collapsed: boolean }) {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ transform: collapsed ? "rotate(-90deg)" : undefined, transition: "transform .15s" }}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}
